/**
 * Mutations for the [users store]{link module:store/users}
 *
 * @module store/users/mutations
 */
import { state as initState, Permission, PERMISSION_FLAGS } from '@/store/modules/users'

/**
 * Mutation to reset the users store
 *
 * @function
 *
 */
export function RESET_USERS(state) {
  const newState = initState()
  for (const e in newState) {
    state[e] = newState[e]
  }
}

/**
 * Mutation to add/replace an User {object} into the store
 *
 * Adds the User into store.users
 * Will replace the user information in case the ID is already existing
 *
 * @function
 *
 * @param {object} user has to be a [User]{@link module:store/users.User}
 *
 */
export function SET_USER(state, user) {
  state.users[user.id] = user
}

/**
 * Mutation to set all Users {object} into the store
 *
 * Sets the Users into store.users
 * Will replace any specific store.users[userId] information already set
 *
 * @function
 *
 * @param {object} users is a hash of users with mapping `userId => User`
 *
 */
export function SET_USERS(state, users) {
  for (const userId in users) {
    state.users[userId] = users[userId]
  }
}

/**
 * Mutation to set all Users {object} directly related to an EMS into the store
 *
 * Sets the Users into store.emss.direct[emsId]
 * Will replace any current store.emss.direct[emsId] information
 *
 * @function
 *
 * @param {Integer} emsId
 *
 * @param {object} users, mapping [userId] => user, including any permissions
 *
 */
export function SET_USERS_FOR_EMS_DIRECT(state, { emsId, users }) {
  state.emss.direct[emsId] = users
}

/**
 * Mutation to set all Users {object} directly related to EMSs into the store
 *
 * Sets the EMSs => Users realtion into store.emss.direct
 * Will replace any current store.emss.direct information
 *
 * @function
 *
 * @param {object} usersForEmss, mapping [emsId] => mapping => [userId] => user, including any permissions
 *
 */
export function SET_USERS_FOR_EMSS_DIRECT(state, usersForEmss) {
  state.emss.direct = usersForEmss
}

/**
 * Mutation to set all Users {object} related to an EMS through emsGroups into the store
 *
 * Sets the Users into store.emss.throughGroup[emsId]
 * Will replace any current store.emss.throughGroup[emsId] information
 *
 * @function
 *
 * @param {Integer} emsId
 *
 * @param {object} users, mapping [userId] => user, including aggregated permissions and related emsGroupIds
 *
 */
export function SET_USERS_FOR_EMS_THROUGH_GROUPS(state, { emsId, users }) {
  state.emss.throughGroups[emsId] = users
}

/**
 * Mutation to set all Users {object} for an emsGroup into the store
 *
 * Sets the Users into store.emsGroups[groupId]
 * Will ignore/leave existing permission of a role, if not present in the input.
 * Deletes all user-entries existing in the store but not in input param `users`.
 *
 * @function
 *
 * @param {object} params
 * @param {Integer} params.groupId
 * @param {object} params.users has to be a map `[userId] => {Permission}`, including any permissions
 * @param {boolean} params.addTrivialPermission if true, a permission flag `0` will be set by default for each role of the user, if the input user's permission for this role is NOT present.
 * @param {boolean} params.preserveExistingPermission if true, a provided permission will be added to any existing permission
 *
 */
export function SET_USERS_FOR_GROUP(
  state,
  { groupId, users, addTrivialPermission = false, preserveExistingPermission = false, removeUnmentioned = true }
) {
  if (!state.emsGroups[groupId]) {
    state.emsGroups[groupId] = users
    return
  }

  const userIds = Object.keys(users)
  const removedUserIds = Object.keys(state.emsGroups[groupId]).filter((i) => !userIds.includes(i))

  if (removeUnmentioned) {
    for (const removedUserId of removedUserIds) {
      delete state.emsGroups[groupId][removedUserId]
    }
  }

  for (const userId of userIds) {
    // check if any permission already exists
    if (!state.emsGroups[groupId][userId]) {
      state.emsGroups[groupId][userId] = new Permission()
    }

    const permission = users[userId]
    for (const role in users[userId]) {
      // ignore not-set new permissions
      if (permission[role] === undefined) {
        continue
      }

      if (state.emsGroups[groupId][userId][role] === undefined) {
        state.emsGroups[groupId][userId][role] = permission[role]
      } else if (preserveExistingPermission) {
        state.emsGroups[groupId][userId][role] = state.emsGroups[groupId][userId][role] | permission[role]
      } else {
        state.emsGroups[groupId][userId][role] = permission[role]
      }
    }

    const user = state.users[userId]
    if (addTrivialPermission && user && user.roles) {
      user.roles.forEach((r) => {
        if (state.emsGroups[groupId][userId][r] === undefined) {
          state.emsGroups[groupId][userId][r] = 0
        }
      })
    }
  }
}

/**
 * Removes all group IDs from the `emsGroup` store, which are not preserved/protected by the input parameter.
 *
 * @function
 *
 * @param {object} params
 * @param {integer[]|string[]} params.preservedGroupIds is the list of group IDs to NOT be removed from the state
 *
 */
export function CLEANUP_GROUPS(state, { preservedGroupIds = [] } = {}) {
  preservedGroupIds = preservedGroupIds.map((i) => i.toString())
  for (const gId in state.emsGroups) {
    if (preservedGroupIds.includes(gId)) {
      continue
    }

    delete state.emsGroups[gId]
  }
}

/*
 * Mutation to set all `{object} users` (`userId => {Permission}`) for emsGroups into the store
 *
 * Sets the emsGroup => Users relation into store.emsGroups
 * Will replace any current store.emsGroups information
 *
 * @function
 *
 * @param {object} usersForGroups, mapping `[emsGroupId] => (mapping: [userId] => {Permission})`
 *
 */
export function SET_USERS_FOR_GROUPS(state, usersForGroups) {
  state.emsGroups = usersForGroups
}

/**
 * Mutation to set all User permissions for a group and role (context) into the store
 *
 * @function
 *
 */
export function SET_ALL_USERROLEPERMS_FOR_GROUP(state, { groupId, role, permVal }) {
  if (state.emsGroups[groupId]) {
    for (const userId in state.emsGroups[groupId]) {
      state.emsGroups[groupId][userId][role] = permVal
    }
  }
}

/**
 * Mutation to toggle the users permissions for a group between the tags hide'|'show'|'admin'
 *
 * Reads the current permissions of a user and changes them based on them
 *
 * @function
 *
 * @params {integer} userId
 * @params {integer} groupId
 *
 */
export function TOGGLE_USER_GROUP_PERMISSION_COMMUNITY(state, { userId, groupId }) {
  if (!state.emsGroups[groupId] || !state.emsGroups[groupId][userId]) {
    return
  }

  state.emsGroups[groupId][userId] = togglePermCommunity(state.emsGroups[groupId][userId])
}

/**
 * Helper Function to return the new permTag based on the given permissions
 *
 * @params {Permissions} perms
 *
 */
const togglePermCommunity = (perms) => {
  if (perms['customer'] & PERMISSION_FLAGS['read']) {
    if (perms['admin'] & PERMISSION_FLAGS['orgRead'] && perms['admin'] & PERMISSION_FLAGS['orgWrite']) {
      // IS ADMIN => SET TO HIDE
      return setToCommunityPerm(perms, 'hide')
    } else {
      // IS SHOW => SET TO ADMIN
      return setToCommunityPerm(perms, 'admin')
    }
  } else {
    //IS HIDE => SET TO SHOW
    return setToCommunityPerm(perms, 'show')
  }
}

/**
 * Helper Function to change the permissions based on the given permTag
 *
 * @params {Permissions} perms
 * @params {String} perm, a permTag 'hide'|'show'|'admin'
 *
 * @return {Permissions} the changed permissions
 */
const setToCommunityPerm = (perms, perm) => {
  const permission = perms

  switch (perm) {
    case 'hide':
      if (perms['customer'] & PERMISSION_FLAGS['read']) {
        permission['customer'] = perms['customer'] ^ PERMISSION_FLAGS['read']
      }
      if (perms['admin'] & PERMISSION_FLAGS['orgRead'] && perms['admin'] & PERMISSION_FLAGS['orgWrite']) {
        permission['admin'] = perms['admin'] ^ PERMISSION_FLAGS['orgRead']
        permission['admin'] = perms['admin'] ^ PERMISSION_FLAGS['orgWrite']
      }
      return permission
    case 'show':
      if (!(perms['customer'] & PERMISSION_FLAGS['read'])) {
        permission['customer'] = perms['customer'] | PERMISSION_FLAGS['read']
      }
      if (perms['admin'] & PERMISSION_FLAGS['orgRead'] && perms['admin'] & PERMISSION_FLAGS['orgWrite']) {
        permission['admin'] = perms['admin'] ^ PERMISSION_FLAGS['orgRead']
        permission['admin'] = perms['admin'] ^ PERMISSION_FLAGS['orgWrite']
      }
      return permission
    case 'admin':
      if (!(perms['customer'] & PERMISSION_FLAGS['read'])) {
        permission['customer'] = perms['customer'] | PERMISSION_FLAGS['read']
      }
      if (!(perms['admin'] & PERMISSION_FLAGS['orgRead'] && perms['admin'] & PERMISSION_FLAGS['orgWrite'])) {
        permission['admin'] = perms['admin'] | PERMISSION_FLAGS['orgRead']
        permission['admin'] = perms['admin'] | PERMISSION_FLAGS['orgWrite']
      }
      return permission
  }
  return permission
}

/**
 * Mutation to set the state of a users permission for a group based on the given Tag
 *
 * @function
 *
 * @params {integer} userId
 * @params {integer} groupId
 * @params {String} perm, 'hide'|'show'|'admin'
 *
 */
export function SET_COMMUNITY_PERM(state, { userId, groupId, perm }) {
  state.emsGroups[groupId][userId] = setToCommunityPerm(state.emsGroups[groupId][userId], perm)
}

export function REMOVE_USER_FROM_GROUP(state, { userId, groupId }) {
  if (state.emsGroups[groupId] && state.emsGroups[groupId][userId]) {
    delete state.emsGroups[groupId][userId]
  }
}

export function EXTEND_CERTIFIED_PARTNER_IDENTIFICATION_DATA(state, { userId, data }) {
  if (!state.users[userId]) return

  state.users[userId].certifiedPartnerIdentification = {
    ...state.users[userId].certifiedPartnerIdentification,
    ...data
  }
}
